// SPDX-License-Identifier: AGPL-3.0-or-later

import {
	buildHermesModelPickerCommands,
	FALLBACK_MODEL_CATALOG,
	type HermesModelCatalog,
	type HermesModelProvider,
	loadHermesModelCatalog,
	providerLabel,
} from '@app/features/devtools/hermesModelCatalog';
import styles from '@app/features/devtools/hermesModelPickerPopout.module.css';
import {Button} from '@app/features/ui/button/Button';
import {Input} from '@app/features/ui/components/form/FormInput';
import {CaretLeftIcon, XIcon} from '@phosphor-icons/react';
import type {ReactNode, RefObject} from 'react';
import {useEffect, useMemo, useRef, useState} from 'react';

interface Props {
	onClose: () => void;
	onSubmitCommands: (commands: Array<string>) => void;
}

type Stage = 'provider' | 'model' | 'effort' | 'scope';

const PICKER_STAGES: ReadonlyArray<Stage> = ['provider', 'model', 'effort', 'scope'];

export function HermesModelPickerPopout({onClose, onSubmitCommands}: Props) {
	const [stage, setStage] = useState<Stage>('provider');
	const [providers, setProviders] = useState<ReadonlyArray<HermesModelProvider>>(FALLBACK_MODEL_CATALOG.providers);
	const [efforts, setEfforts] = useState<ReadonlyArray<string>>(FALLBACK_MODEL_CATALOG.efforts);
	const [loadingCatalog, setLoadingCatalog] = useState(true);
	const [provider, setProvider] = useState<HermesModelProvider | null>(null);
	const [model, setModel] = useState<string | null>(null);
	const [effort, setEffort] = useState('medium');
	const [query, setQuery] = useState('');
	const scopeSessionRef = useRef<HTMLButtonElement>(null);
	const queryInputRef = useRef<HTMLInputElement>(null);
	const queryRef = useRef('');
	const popoutRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		let alive = true;
		setLoadingCatalog(true);
		void loadHermesModelCatalog(undefined, true)
			.then((catalog: HermesModelCatalog) => {
				if (alive) {
					setProviders(catalog.providers);
					setEfforts(catalog.efforts);
				}
			})
			.finally(() => {
				if (alive) setLoadingCatalog(false);
			});
		return () => {
			alive = false;
		};
	}, []);

	const focusFirstOption = () => {
		requestAnimationFrame(() => {
			if (document.activeElement === queryInputRef.current) return;
			const first = popoutRef.current?.querySelector<HTMLButtonElement>('[data-hermes-picker-option="true"]');
			first?.focus();
		});
	};

	useEffect(() => {
		queryRef.current = '';
		setQuery('');
		if (stage === 'scope') {
			requestAnimationFrame(() => {
				if (document.activeElement !== queryInputRef.current) scopeSessionRef.current?.focus();
			});
		} else {
			focusFirstOption();
		}
	}, [stage]);

	// The catalog is loaded asynchronously. On the first render the search input
	// receives autoFocus, and the provider buttons may not exist yet. Refocus the
	// first real option once the provider list has arrived so ArrowUp/ArrowDown
	// work immediately when /models opens.
	useEffect(() => {
		if (stage !== 'scope' && !loadingCatalog) focusFirstOption();
	}, [stage, loadingCatalog, providers.length, efforts.length]);

	const updateQuery = (next: string) => {
		queryRef.current = next;
		setQuery(next);
	};
	const ensureSearchInputFocus = () => {
		requestAnimationFrame(() => queryInputRef.current?.focus());
	};
	const levenshtein = (left: string, right: string) => {
		const row = Array.from({length: right.length + 1}, (_, index) => index);
		for (let i = 1; i <= left.length; i += 1) {
			let diagonal = row[0];
			row[0] = i;
			for (let j = 1; j <= right.length; j += 1) {
				const above = row[j];
				row[j] = Math.min(row[j] + 1, row[j - 1] + 1, diagonal + (left[i - 1] === right[j - 1] ? 0 : 1));
				diagonal = above;
			}
		}
		return row[right.length];
	};
	const matchScore = (value: string, search: string): number | null => {
		const normalized = value.toLowerCase();
		if (!search) return 0;
		if (normalized === search) return 0;
		if (normalized.startsWith(search)) return 1;
		if (normalized.includes(search)) return 2;
		const candidates = normalized
			.split(/[^a-z0-9]+/i)
			.filter(Boolean)
			.concat(
				Array.from({length: Math.max(0, normalized.length - search.length + 1)}, (_, index) =>
					normalized.slice(index, index + search.length),
				),
			);
		const distance = Math.min(...candidates.map((candidate) => levenshtein(candidate, search)));
		return distance <= Math.max(1, Math.ceil(search.length * 0.35)) ? 10 + distance : null;
	};

	const filteredProviders = useMemo(
		() =>
			providers
				.map((item) => ({item, score: matchScore(`${item.name} ${item.slug}`, query.trim().toLowerCase())}))
				.filter((entry): entry is {item: HermesModelProvider; score: number} => entry.score !== null)
				.sort((a, b) => a.score - b.score)
				.map((entry) => entry.item),
		[providers, query],
	);
	const filteredModels = useMemo(
		() =>
			(provider?.models ?? [])
				.map((item) => ({item, score: matchScore(item, query.trim().toLowerCase())}))
				.filter((entry): entry is {item: string; score: number} => entry.score !== null)
				.sort((a, b) => a.score - b.score)
				.map((entry) => entry.item),
		[provider, query],
	);
	// The list container is capped to roughly five rows / one third of the
	// viewport. Keep all entries reachable by scrolling or search; do not drop
	// live providers/models just because they are below the fold.
	const visibleProviders = filteredProviders;
	const visibleModels = filteredModels;
	const visibleEfforts = efforts
		.map((item) => ({item, score: matchScore(item, query.trim().toLowerCase())}))
		.filter((entry): entry is {item: string; score: number} => entry.score !== null)
		.sort((a, b) => a.score - b.score)
		.map((entry) => entry.item);
	const visibleScopes = [
		{
			id: 'session',
			title: 'Nur für diese Sitzung',
			subtitle: 'Der globale Standard bleibt unverändert.',
			persistence: 'session' as const,
		},
		{
			id: 'global',
			title: 'Global speichern',
			subtitle: 'Als neuer Hermes-Standard übernehmen.',
			persistence: 'global' as const,
		},
	]
		.map((item) => ({item, score: matchScore(`${item.title} ${item.id}`, query.trim().toLowerCase())}))
		.filter((entry) => entry.score !== null)
		.sort((a, b) => (a.score ?? Number.POSITIVE_INFINITY) - (b.score ?? Number.POSITIVE_INFINITY))
		.map((entry) => entry.item);

	const selectProvider = (next: HermesModelProvider) => {
		setProvider(next);
		setStage('model');
	};
	const selectModel = (next: string) => {
		setModel(next);
		setStage('effort');
	};
	const goBack = () => {
		const index = PICKER_STAGES.indexOf(stage);
		if (index === 0) {
			onClose();
			return;
		}
		setStage(PICKER_STAGES[index - 1]);
	};
	const apply = (nextPersistence: 'session' | 'global') => {
		if (!provider || !model) {
			onClose();
			return;
		}
		onSubmitCommands(
			buildHermesModelPickerCommands({provider: provider.slug, model, effort, persistence: nextPersistence}),
		);
		onClose();
	};

	const title: Record<Stage, string> = {
		provider: 'Hermes · Provider',
		model: `Hermes · ${provider?.name ?? 'Modell'}`,
		effort: `Hermes · ${model ?? 'Effort'}`,
		scope: 'Hermes · Übernehmen',
	};
	const list = (
		items: Array<{
			id: string;
			title: string;
			subtitle?: string;
			onSelect: () => void;
			dataFlx?: string;
			buttonRef?: RefObject<HTMLButtonElement | null>;
		}>,
		isScope = false,
	) => (
		<div className={`${styles.list} ${isScope ? styles.scopeList : ''}`} role="listbox" aria-busy={loadingCatalog}>
			{items.map((item, index) => (
				<button
					key={item.id}
					type="button"
					className={`${styles.option} ${isScope ? styles.scopeOption : ''}`}
					onClick={item.onSelect}
					role="option"
					data-hermes-picker-option="true"
					ref={(element) => {
						if (index === 0 && element && stage === 'provider' && !loadingCatalog && !queryRef.current) element.focus();
						if (item.buttonRef) item.buttonRef.current = element;
					}}
					data-flx={item.dataFlx}
				>
					<span className={styles.optionTitle}>{item.title}</span>
					{item.subtitle && <span className={styles.optionSubtitle}>{item.subtitle}</span>}
				</button>
			))}
			{items.length === 0 && (
				<div className={styles.empty}>
					{loadingCatalog ? 'Modelle werden aktualisiert…' : 'Keine Einträge verfügbar.'}
				</div>
			)}
		</div>
	);

	let content: ReactNode;
	if (stage === 'provider') {
		content = list(
			visibleProviders.map((item) => ({
				id: item.slug,
				title: item.name,
				subtitle: providerLabel(item),
				onSelect: () => selectProvider(item),
			})),
		);
	} else if (stage === 'model') {
		content = list(visibleModels.map((item) => ({id: item, title: item, onSelect: () => selectModel(item)})));
	} else if (stage === 'effort') {
		content = list(
			visibleEfforts.map((item) => ({
				id: item,
				title: item,
				subtitle: item === 'none' ? 'Thinking deaktiviert' : 'Reasoning-Effort',
				onSelect: () => {
					setEffort(item);
					setStage('scope');
				},
			})),
		);
	} else {
		content = list(
			visibleScopes.map((item) => ({
				id: item.id,
				title: item.title,
				subtitle: item.subtitle,
				onSelect: () => apply(item.persistence),
				dataFlx: `devtools.hermes-model-picker.scope-${item.id}`,
				buttonRef: item.id === 'session' ? scopeSessionRef : undefined,
			})),
			true,
		);
	}

	return (
		<div
			ref={popoutRef}
			tabIndex={-1}
			className={styles.popout}
			role="dialog"
			aria-label="Hermes Modell auswählen"
			onKeyDown={(event) => {
				if (!event.ctrlKey && !event.metaKey && !event.altKey) {
					if (document.activeElement !== queryInputRef.current) {
						if (event.key.length === 1 && event.key !== ' ') {
							updateQuery(`${queryRef.current}${event.key}`);
							ensureSearchInputFocus();
							event.preventDefault();
							event.stopPropagation();
							return;
						}
						if (event.key === 'Backspace') {
							updateQuery(queryRef.current.slice(0, -1));
							ensureSearchInputFocus();
							event.preventDefault();
							event.stopPropagation();
							return;
						}
					}
				}
				if (event.key === 'Escape') {
					event.preventDefault();
					event.stopPropagation();
					onClose();
					return;
				}
				const options = Array.from(
					event.currentTarget.querySelectorAll<HTMLButtonElement>('[data-hermes-picker-option="true"]'),
				);
				if (!options.length) return;
				const current = options.indexOf(document.activeElement as HTMLButtonElement);
				if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
					event.preventDefault();
					event.stopPropagation();
					const delta = event.key === 'ArrowDown' ? 1 : -1;
					const next =
						current === -1
							? event.key === 'ArrowDown'
								? 0
								: options.length - 1
							: (current + delta + options.length) % options.length;
					options[next]?.focus();
					return;
				}
				if (event.key === 'Home' || event.key === 'End') {
					event.preventDefault();
					options[event.key === 'Home' ? 0 : options.length - 1]?.focus();
					return;
				}
				if (event.key === 'Enter' || event.key === ' ') {
					const focused = document.activeElement as HTMLButtonElement;
					if (options.includes(focused)) {
						event.preventDefault();
						event.stopPropagation();
						focused.click();
					}
				}
			}}
		>
			<div className={styles.header}>
				<Button
					square
					icon={stage === 'provider' ? <XIcon /> : <CaretLeftIcon />}
					onClick={goBack}
					aria-label={stage === 'provider' ? 'Abbrechen' : 'Zurück'}
				/>
				<strong>{title[stage]}</strong>
				<span className={styles.progress}>{PICKER_STAGES.indexOf(stage) + 1}/4</span>
			</div>
			{
				<Input
					ref={queryInputRef}
					value={query}
					onChange={(event) => updateQuery(event.target.value)}
					placeholder="Suchen…"
					aria-label="Hermes-Auswahl durchsuchen"
				/>
			}
			{content}
			{stage === 'scope' && (
				<div className={styles.footer}>
					<span>Modell: {model}</span>
					<span>Effort: {effort}</span>
				</div>
			)}
		</div>
	);
}

export default HermesModelPickerPopout;
